import React from 'react';
import { Clock, Flame, AlertCircle, Volume2, ArrowRight, CheckCircle2, Dumbbell } from 'lucide-react';
import { triggerHAWorkoutAlert } from '../../lib/homeAssistant';

interface WorkoutCutoffProps {
  cutoffTime: string;
  workoutDone: boolean;
  onNavigateToWorkout: () => void;
}

/**
 * 1. Card de Horário Limite do Treino (O card inteiro muda de cor, não apenas uma bolinha)
 */
export const WorkoutCutoffCard: React.FC<WorkoutCutoffProps> = ({
  cutoffTime,
  workoutDone,
  onNavigateToWorkout,
}) => {
  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  const [cutoffH, cutoffM] = cutoffTime.split(':').map(Number);

  const isPastCutoff = currentHour > cutoffH || (currentHour === cutoffH && currentMinute >= cutoffM);
  const isUrgent = !workoutDone && isPastCutoff;

  const handleTriggerAlarm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await triggerHAWorkoutAlert();
  };

  // CASO 1: HORÁRIO LIMITE EXCEDIDO (Alerta Vermelho Emergencial em todo o card)
  if (isUrgent) {
    return (
      <div 
        onClick={onNavigateToWorkout}
        className="rounded-[28px] p-5 border border-[#FF453A]/80 bg-gradient-to-r from-[#FF453A]/25 via-[#FF375F]/20 to-[#FF453A]/25 text-white shadow-[0_4px_24px_rgba(255,69,58,0.3)] animate-pulse flex items-start gap-3.5 cursor-pointer apple-press"
      >
        <div className="p-3 rounded-[20px] bg-[#FF453A]/30 text-[#FF453A] border border-[#FF453A]/40 shrink-0">
          <AlertCircle className="w-6 h-6" />
        </div>

        <div className="flex-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-extrabold text-white uppercase tracking-wider text-xs">
              🚨 Horário Limite Excedido ({cutoffTime.slice(0, 5)})
            </span>
            <span className="text-[10px] tabular-nums font-bold px-2.5 py-0.5 rounded-full bg-[#FF453A]/40 text-white border border-[#FF453A]/50">
              URGENTE
            </span>
          </div>

          <p className="text-white/90 mt-1.5 leading-relaxed font-medium">
            Passou das {cutoffTime.slice(0, 5)} e você ainda não iniciou o treino. 
            Sem treino não há recomposição!
          </p>

          <div className="mt-3">
            <button
              onClick={onNavigateToWorkout}
              className="w-full py-3 px-4 rounded-[18px] bg-white text-black font-black uppercase tracking-wider text-xs shadow-md transition active:scale-95 flex items-center justify-center gap-2 cursor-pointer apple-press"
            >
              <span>Iniciar Treino Agora</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CASO 2: TREINO CONCLUÍDO (Card inteiro Apple Green)
  if (workoutDone) {
    return (
      <div 
        onClick={onNavigateToWorkout}
        className="rounded-[28px] p-4 border border-[#30D158]/50 bg-gradient-to-r from-[#30D158]/20 via-[#28C846]/15 to-[#30D158]/20 text-slate-900 dark:text-[#30D158] dark:bg-[#30D158]/10 shadow-sm cursor-pointer transition-all duration-200 apple-press"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-[18px] bg-[#30D158]/25 text-[#30D158] border border-[#30D158]/35">
              <CheckCircle2 className="w-5 h-5" />
            </div>

            <div>
              <div className="text-xs font-extrabold uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                <span>Treino Concluído com Sucesso!</span>
              </div>
              <div className="text-[11px] text-slate-600 dark:text-slate-300 font-medium mt-0.5">
                Série registrada hoje • Disciplina mantida
              </div>
            </div>
          </div>

          <span className="text-[10px] tabular-nums font-bold px-2.5 py-1 rounded-full bg-[#30D158]/25 text-[#30D158] border border-[#30D158]/40">
            CONCLUÍDO ✓
          </span>
        </div>
      </div>
    );
  }

  // CASO 3: TREINO PENDENTE (Card inteiro com cor Apple Orange de atenção)
  return (
    <div 
      onClick={onNavigateToWorkout}
      className="rounded-[28px] p-4 border border-[#FF9F0A]/50 bg-gradient-to-r from-[#FF9F0A]/15 via-[#FF9F0A]/10 to-[#FF9F0A]/15 text-slate-900 dark:text-[#FF9F0A] dark:bg-[#FF9F0A]/10 shadow-sm cursor-pointer hover:border-[#FF9F0A]/80 transition-all duration-200 apple-press"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-[18px] bg-[#FF9F0A]/25 text-[#FF9F0A] border border-[#FF9F0A]/35">
            <Clock className="w-5 h-5" />
          </div>

          <div>
            <div className="text-xs font-extrabold uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
              <span>Horário Limite do Treino:</span>
              <span className="tabular-nums text-[#FF9F0A] text-sm font-extrabold">{cutoffTime.slice(0, 5)}</span>
            </div>
            <div className="text-[11px] text-slate-600 dark:text-slate-300 font-medium mt-0.5">
              Sessão pendente hoje • Toque aqui para treinar
            </div>
          </div>
        </div>

        <span className="text-[10px] tabular-nums font-bold px-2.5 py-1 rounded-full bg-[#FF9F0A]/25 text-[#FF9F0A] border border-[#FF9F0A]/40">
          PENDENTE
        </span>
      </div>
    </div>
  );
};

interface StreakDisciplineCardProps {
  streakDays: number;
  waterGoalReached: boolean;
  workoutDone: boolean;
  onNavigateToWorkout: () => void;
}

/**
 * 2. Card de Disciplina Diária (O card INTEIRO fica na cor da fase)
 * Verde (1-2 dias) -> Amarelo (3-4 dias) -> Vermelho (5-6 dias) -> Roxo (7+ dias)
 */
export const StreakDisciplineCard: React.FC<StreakDisciplineCardProps> = ({
  streakDays,
  waterGoalReached,
  workoutDone,
  onNavigateToWorkout,
}) => {
  const getStreakTheme = (days: number) => {
    if (days <= 2) {
      return {
        levelName: 'Nível Verde • Iniciação (1-2d)',
        container: 'bg-[#30D158]/15 border border-[#30D158]/50 text-slate-900 dark:text-white shadow-sm',
        badge: 'bg-[#30D158]/25 text-[#30D158] border border-[#30D158]/40',
        subCard: 'bg-white/60 dark:bg-[#1C1C1E]/80 border border-[#30D158]/30 text-slate-900 dark:text-white',
        flameColor: 'fill-[#30D158] text-[#30D158]',
      };
    } else if (days <= 4) {
      return {
        levelName: 'Nível Amarelo • Consistência (3-4d)',
        container: 'bg-[#FFD60A]/15 border border-[#FFD60A]/50 text-slate-900 dark:text-white shadow-sm',
        badge: 'bg-[#FFD60A]/25 text-[#FFD60A] border border-[#FFD60A]/40',
        subCard: 'bg-white/60 dark:bg-[#1C1C1E]/80 border border-[#FFD60A]/30 text-slate-900 dark:text-white',
        flameColor: 'fill-[#FFD60A] text-[#FFD60A]',
      };
    } else if (days <= 6) {
      return {
        levelName: 'Nível Vermelho • Intensidade (5-6d)',
        container: 'bg-[#FF453A]/15 border border-[#FF453A]/50 text-slate-900 dark:text-white shadow-sm',
        badge: 'bg-[#FF453A]/25 text-[#FF453A] border border-[#FF453A]/40',
        subCard: 'bg-white/60 dark:bg-[#1C1C1E]/80 border border-[#FF453A]/30 text-slate-900 dark:text-white',
        flameColor: 'fill-[#FF453A] text-[#FF453A]',
      };
    } else {
      // 7+ dias: Roxo nobre Apple System Purple
      return {
        levelName: 'Nível Roxo • Disciplina Lendária (7d+)',
        container: 'bg-[#BF5AF2]/15 border border-[#BF5AF2]/60 text-slate-900 dark:text-white shadow-[0_4px_24px_rgba(191,90,242,0.25)]',
        badge: 'bg-[#BF5AF2]/25 text-[#BF5AF2] border border-[#BF5AF2]/40',
        subCard: 'bg-white/60 dark:bg-[#1C1C1E]/80 border border-[#BF5AF2]/30 text-slate-900 dark:text-white',
        flameColor: 'fill-[#BF5AF2] text-[#BF5AF2]',
      };
    }
  };

  const theme = getStreakTheme(streakDays);

  return (
    <div className={`rounded-[28px] p-5 transition-all duration-300 ${theme.container}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Flame className={`w-5 h-5 ${theme.flameColor} animate-bounce`} />
          <div>
            <h3 className="text-xs font-black uppercase tracking-wider">
              Disciplina Diária
            </h3>
            <span className="text-[11px] tabular-nums font-bold opacity-80">
              {streakDays} {streakDays === 1 ? 'Dia Seguido' : 'Dias Seguidos'}
            </span>
          </div>
        </div>

        <span className={`text-[10px] tabular-nums font-bold px-2.5 py-1 rounded-full ${theme.badge}`}>
          {theme.levelName}
        </span>
      </div>

      {/* Grid das Metas Diárias imersas na cor */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className={`p-3.5 rounded-[20px] ${theme.subCard}`}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className={`w-4 h-4 shrink-0 ${waterGoalReached ? 'opacity-100 text-[#30D158]' : 'opacity-40'}`} />
            <div>
              <div className="font-bold text-xs">5.000ml Água</div>
              <div className="text-[10px] opacity-75 mt-0.5">
                {waterGoalReached ? '100% Batida' : 'Em andamento'}
              </div>
            </div>
          </div>
        </div>

        <div 
          onClick={onNavigateToWorkout}
          className={`p-3.5 rounded-[20px] cursor-pointer hover:opacity-90 transition apple-press ${theme.subCard}`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className={`w-4 h-4 shrink-0 ${workoutDone ? 'opacity-100 text-[#30D158]' : 'opacity-40'}`} />
            <div>
              <div className="font-bold text-xs">Treino de Força</div>
              <div className="text-[10px] opacity-75 mt-0.5">
                {workoutDone ? 'Séries concluídas' : 'Clique p/ treinar'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
